export type TableUiStatus = "vacant" | "occupied" | "reserved" | "blocked";

export interface Table {
  id: string;
  name: string;
  status: TableUiStatus;
  backendStatus: string;
  currentSaleId?: string | null;
  seats: number;
  section?: string | null;
}

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  notes?: string;
  gstPercentage?: number;
  isTaxInclusive?: boolean;
  /** Already on bill / KOT sent — read-only in order list */
  locked?: boolean;
  kotNumber?: string | null;
  kotStatus?: string | null;
}

export interface Product {
  id: string;
  productMasterId: string;
  name: string;
  salePrice: number;
  category: string;
  image?: string;
  gstPercentage?: number;
  isTaxInclusive?: boolean;
}

export interface CustomerDetails {
  name: string;
  phone: string;
  email: string;
  customerId?: string | null;
}

export const mapTableStatus = (status: string): TableUiStatus => {
  switch (status) {
    case "OCCUPIED":
      return "occupied";
    case "RESERVED":
      return "reserved";
    case "BLOCKED":
      return "blocked";
    case "AVAILABLE":
    default:
      return "vacant";
  }
};

/** Normalize API table row — treat completed/stale sales as vacant in UI. */
export const normalizePosTable = (t: {
  id: string;
  name: string;
  status: string;
  capacity: number;
  section?: string | null;
  currentSaleId?: string | null;
  currentSale?: { id?: string; status?: string } | null;
}): Table => {
  const linkedSaleId = t.currentSaleId ?? t.currentSale?.id ?? null;
  const saleIsActive = t.currentSale?.status === "pending";
  const hasActiveBill = Boolean(linkedSaleId && saleIsActive);

  let backendStatus = t.status;
  if (t.status === "OCCUPIED" && !hasActiveBill) {
    backendStatus = "AVAILABLE";
  } else if (
    t.currentSale?.status === "completed" ||
    t.currentSale?.status === "cancelled"
  ) {
    backendStatus = "AVAILABLE";
  }

  return {
    id: t.id,
    name: t.name,
    status: mapTableStatus(backendStatus),
    backendStatus,
    currentSaleId: hasActiveBill ? linkedSaleId : null,
    seats: t.capacity,
    section: t.section,
  };
};
